/**
 * @fileoverview Fotos de perfil dos contatos de um cartão (vCard). Pede ao backend
 * (GET /messages/:ticketId/contact-pictures) só os números que faltam e guarda o
 * resultado em memória por 30 min. Os pedidos dos cartões que aparecem juntos
 * (30 ms) viram um só, em lotes de até 20 números; o mesmo número em vários
 * cartões do ticket é pedido uma vez. Chave ticket + número: a foto depende da
 * conexão do ticket (WAHA tem, a API oficial da Meta não).
 */

import { useEffect, useState } from "react";

import api from "../../services/api";

const TTL_MS = 30 * 60 * 1000;
const BATCH = 20;
const FLUSH_MS = 30;
const cache = new Map(); // "ticketId:número" -> { url, at }
const pending = new Map(); // "ticketId:número" -> Promise (resolve quando a resposta chega)
const queue = new Map(); // ticketId -> Map(número -> resolve)
let flushTimer = null;

const keyOf = (ticketId, number) => `${ticketId}:${number}`;

const isFresh = (key) => {
  const entry = cache.get(key);
  return !!entry && Date.now() - entry.at < TTL_MS;
};

const flush = () => {
  flushTimer = null;
  queue.forEach((byNumber, ticketId) => {
    const numbers = [...byNumber.keys()];
    for (let i = 0; i < numbers.length; i += BATCH) {
      const batch = numbers.slice(i, i + BATCH);
      api
        .get(`/messages/${ticketId}/contact-pictures`, { params: { numbers: batch.join(",") } })
        .then(({ data }) => {
          batch.forEach((n) => cache.set(keyOf(ticketId, n), { url: data?.pictures?.[n] || null, at: Date.now() }));
        })
        .catch(() => {}) // sem foto desta vez; tenta de novo numa próxima montagem
        .finally(() =>
          batch.forEach((n) => {
            pending.delete(keyOf(ticketId, n));
            byNumber.get(n)();
          })
        );
    }
  });
  queue.clear();
};

const load = (ticketId, numbers) => {
  numbers.forEach((n) => {
    const key = keyOf(ticketId, n);
    if (isFresh(key) || pending.has(key)) return;
    let resolve;
    pending.set(key, new Promise((r) => { resolve = r; }));
    if (!queue.has(ticketId)) queue.set(ticketId, new Map());
    queue.get(ticketId).set(n, resolve);
  });
  if (queue.size > 0 && !flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
  return Promise.all(numbers.map((n) => pending.get(keyOf(ticketId, n))).filter(Boolean));
};

/** Devolve pictureOf(número) → URL ou null; só pede quando `enabled`. */
export const useContactPictures = (ticketId, numbers, enabled) => {
  const key = (numbers || []).filter(Boolean).join(",");
  const [, rerender] = useState(0);

  useEffect(() => {
    if (!enabled || !ticketId || !key) return undefined;
    let alive = true;
    load(ticketId, key.split(",")).then(() => {
      if (alive) rerender((v) => v + 1);
    });
    return () => {
      alive = false;
    };
  }, [ticketId, key, enabled]);

  return (number) => {
    const key = keyOf(ticketId, number);
    return number && isFresh(key) ? cache.get(key).url : null;
  };
};
